import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getPortfolioItems } from '../data/portfolioData';
import { Heart, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PortfolioItem = () => {
  const { t } = useTranslation();
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [previousItem, setPreviousItem] = useState(null);
  const [nextItem, setNextItem] = useState(null);

  useEffect(() => {
    // Get translated portfolio items
    const portfolioItems = getPortfolioItems(t);

    const currentItem = portfolioItems.find(p => p.id === itemId);
    if (currentItem) {
      setItem(currentItem);

      // Find previous and next items
      const currentIndex = portfolioItems.findIndex(p => p.id === itemId);
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : portfolioItems.length - 1;
      const nextIndex = currentIndex < portfolioItems.length - 1 ? currentIndex + 1 : 0;

      setPreviousItem(portfolioItems[prevIndex]);
      setNextItem(portfolioItems[nextIndex]);
    }
  }, [itemId, t]);

  if (!item) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-xl text-gray-600">{t('portfolio.notFound')}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-sm text-gray-600">
            <a href="/" className="text-blue-500 hover:underline">AP Pacific</a> &gt;{' '}
            <a href="/portfolio" className="text-blue-500 hover:underline">{t('portfolio.breadcrumb')}</a> &gt; {item.title}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white py-4 border-b">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <Link
            to={`/portfolio-item/${previousItem.id}`}
            className="flex items-center text-gray-600 hover:text-blue-500 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('portfolio.previous')}
          </Link>

          <Link
            to="/portfolio"
            className="text-blue-500 hover:text-blue-700 font-medium"
          >
            {t('portfolio.backToPortfolio')}
          </Link>

          <Link
            to={`/portfolio-item/${nextItem.id}`}
            className="flex items-center text-gray-600 hover:text-blue-500 transition-colors"
          >
            {t('portfolio.next')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </div>

      {/* Project Header */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              {item.category}
            </div>
            
            <h1 className="text-4xl font-bold text-gray-800 mb-4">{item.title}</h1>
            
            <div className="flex items-center justify-center space-x-6 text-gray-600">
              <div className="flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                {item.date}
              </div>
              <div className="flex items-center">
                <Heart className="w-5 h-5 mr-2 text-red-500" />
                {item.likes} {t('portfolio.likes')}
              </div>
            </div>
          </div>

          {/* Project Image */}
          <div className="mb-8">
            <img 
              src={item.image} 
              alt={item.title}
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Project Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {item.description}
                </p>
                
                <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {item.content}
                </div>

                {/* Additional Images */}
                {item.additionalImages && item.additionalImages.length > 0 && (
                  <div className="mt-8">
                    <div className="grid grid-cols-1 gap-6">
                      {item.additionalImages.map((imageUrl, index) => (
                        <img
                          key={index}
                          src={imageUrl}
                          alt={`${item.title} - Image ${index + 1}`}
                          className="w-full rounded-lg shadow-md"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Before & After Images */}
                {item.imageBefore && item.imageAfter && (
                  <div className="mt-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-bold text-gray-800 mb-3">Before</h4>
                        <img
                          src={item.imageBefore}
                          alt="Before cleaning"
                          className="w-full rounded-lg shadow-md"
                        />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-800 mb-3">After</h4>
                        <img
                          src={item.imageAfter}
                          alt="After cleaning"
                          className="w-full rounded-lg shadow-md"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {item.beforeAfter && (
                  <div className="mt-8 p-6 bg-green-50 rounded-lg">
                    <h3 className="text-xl font-bold text-green-800 mb-2">
                      {t('portfolio.exceptionalResults')}
                    </h3>
                    <p className="text-green-700">
                      {t('portfolio.exceptionalText')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Project Details Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 p-6 rounded-lg sticky top-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">{t('portfolio.projectDetails')}</h3>
                
                <div className="space-y-3">
                  {Object.entries(item.projectDetails).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="font-medium text-gray-600">{key}:</span>
                      <span className="text-gray-800">{value}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Link
                    to="/contact"
                    className="block w-full bg-blue-500 text-white text-center py-3 px-4 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                  >
                    {t('portfolio.requestService')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects Navigation */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">{t('portfolio.relatedProjects')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Previous Project */}
            <Link
              to={`/portfolio-item/${previousItem.id}`}
              className="group block bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              <img
                src={previousItem.image}
                alt={previousItem.title}
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{previousItem.title}</h3>
                <p className="text-gray-600">{previousItem.description}</p>
              </div>
            </Link>

            {/* Next Project */}
            <Link
              to={`/portfolio-item/${nextItem.id}`}
              className="group block bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              <img
                src={nextItem.image}
                alt={nextItem.title}
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{nextItem.title}</h3>
                <p className="text-gray-600">{nextItem.description}</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortfolioItem;